import { DecimalDataEditor } from "../Editors/DecimalDataEditor";
import { DateTimeDataEditor } from "../Editors/DateTimeDataEditor";
import { MultipleChoiceDataEditor } from "../Editors/MultipleChoiceDataEditor";
import { NoteDataEditor } from "../Editors/NoteDataEditor";
import { MultipleChoiceExtendedComponentSelector } from "../MultipleChoiceExtended/MultipleChoiceExtendedComponentSelector";
import { ComponentModelDTO, ComponentType, UserDTO } from "groopit-js";
import React from "react";
import { ComponentDataDTO } from "groopit-js/client";

interface ComponentEditorProps {
  component?: ComponentDataDTO;
  model: ComponentModelDTO;
  baseName: string;
  groupId: string;
  tags?: { [id: string]: UserDTO };
  errors?: string[];
}

export const GroopitComponentEditor = (props: ComponentEditorProps) => {
  const { component, model, baseName, groupId, tags, errors } = { ...props };

  const editor = () => {
    if (model.description === undefined || model.formId === undefined) {
      throw new Error("Component model is missing required fields");
    }

    switch (model.type) {
      case ComponentType.Note: {
        return (
          <NoteDataEditor
            description={model.description ?? model.type.toString()}
            disabled={false}
            baseName={baseName}
            groupId={groupId}
            tags={tags}
            rawText={component?.notes}
          />
        );
      }
      case ComponentType.MultipleChoice: {
        if (
          model.multiple === undefined ||
          model.options === undefined ||
          model.userEditable === undefined
        ) {
          throw new Error(
            "MultipleChoice component model is missing required fields",
          );
        }
        return (
          <MultipleChoiceDataEditor
            baseName={baseName}
            disabled={false}
            multiple={model.multiple}
            description={model.description}
            optionList={model.options.split("|")}
            allowUpdates={model.userEditable}
            choices={component?.options?.split("|") ?? []}
          />
        );
      }
      case ComponentType.DateTime: {
        if (model.dateOnly === undefined) {
          throw new Error(
            "DateTime component model is missing required fields",
          );
        }
        return (
          <DateTimeDataEditor
            baseName={baseName}
            disabled={false}
            description={model.description}
            dateOnly={model.dateOnly}
            sideBySide={false}
            timestamp={component?.timestamp?.toISOString()}
          />
        );
      }
      case ComponentType.FixedDecimal: {
        return (
          <DecimalDataEditor
            baseName={baseName}
            disabled={false}
            description={model.description}
            places={model.options ? parseInt(model.options.slice(1)) : 0}
            format={model.options ? model.options[0] : "F"}
            number={component?.number}
          />
        );
      }
      case ComponentType.MultipleChoiceExtended: {
        if (model.multiple === undefined) {
          throw new Error(
            "MultipleChoiceExtended component model is missing required fields",
          );
        }
        return (
          <MultipleChoiceExtendedComponentSelector
            assignmentId={model.formId}
            baseName={baseName}
            choice={component?.choice}
            componentModelId={model.componentModel_Id}
            description={model.description}
            multipleSelect={model.multiple}
          />
        );
      }
      default:
        return <div>Unknown type {model.type}</div>;
    }
  };

  return (
    <div className="groop-item">
      <input
        type="hidden"
        name={`${baseName}.ComponentModel_Id`}
        value={model.componentModel_Id}
      />
      <input type="hidden" name={`${baseName}.Type`} value={model.type} />
      <input
        type="hidden"
        name={`${baseName}.Required`}
        value={model.required ? "true" : "false"}
      />
      <input
        type="hidden"
        name={`${baseName}.Description`}
        value={model.description}
      />
      <input
        type="hidden"
        name={`${baseName}.HiddenFromMembers`}
        value={model.hiddenFromMembers ? "true" : "false"}
      />
      <input
        type="hidden"
        name={`${baseName}.ParentComponent`}
        value={model.parentModelComponentModel_Id ?? ""}
      />
      {model.hiddenFromMembers && (
        <div className="text-display-caption text-color-accent">
          Data will only be visible to the group organizer.
        </div>
      )}
      {editor()}
      {errors && (
        <ul className="text-danger">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
